import { Injectable } from '@angular/core';

import { environment } from '@environments/environment';

type GoCardlessEnvironment = 'live' | 'sandbox';

interface GoCardlessDropinHandler {
    open(): void;
}

interface GoCardlessDropinSdk {
    create(options: { billingRequestFlowID: string; environment: GoCardlessEnvironment; onSuccess: () => void; onExit: () => void }): GoCardlessDropinHandler;
}

interface GoCardlessDropinOptions {
    billingRequestFlowId: string;
    environment?: GoCardlessEnvironment;
    onSuccess: () => void;
    onExit: () => void;
}

// Loads and drives the official GoCardless Dropin (TD-16969 FE): an embedded modal/overlay rendered on top of the
// checkout via the GoCardless JS SDK, keyed by a billing request flow id. It keeps the member on the page (no
// redirect), so the cart/purchase survives, and works inside the mobile app WebView. If the SDK cannot load (CSP,
// offline, blocked), `load()` resolves false and the caller falls back to the hosted-page redirect.
@Injectable({
    providedIn: 'root'
})
export class GoCardlessDropinService {
    private static readonly SCRIPT_URL = 'https://pay.gocardless.com/billing/static/dropin/v2/initialise.js';

    private scriptPromise: Promise<boolean> | null = null;

    load(): Promise<boolean> {
        if (this.scriptPromise) {
            return this.scriptPromise;
        }

        this.scriptPromise = new Promise<boolean>((resolve) => {
            if (this.dropin) {
                resolve(true);

                return;
            }

            const script = document.createElement('script');
            script.src = GoCardlessDropinService.SCRIPT_URL;
            script.async = true;
            // NB: do NOT set crossOrigin — the GoCardless Dropin CDN does not return CORS headers, so
            // crossOrigin='anonymous' makes the browser reject the script (verified). A plain script tag loads it
            // fine. (SRI is therefore not possible either; GoCardless does not publish a hash for the v2 Dropin.)
            script.onload = () => resolve(!!this.dropin);
            script.onerror = () => {
                // Allow a later retry if the script failed to load (e.g. transient network / CSP not yet allowed).
                this.scriptPromise = null;
                resolve(false);
            };
            document.body.appendChild(script);
        });

        return this.scriptPromise;
    }

    open(options: GoCardlessDropinOptions): boolean {
        const dropin = this.dropin;
        if (!dropin) {
            return false;
        }

        const handler = dropin.create({
            billingRequestFlowID: options.billingRequestFlowId,
            environment: options.environment ?? (environment.production ? 'live' : 'sandbox'),
            onSuccess: options.onSuccess,
            onExit: options.onExit
        });
        handler.open();

        return true;
    }

    private get dropin(): GoCardlessDropinSdk | undefined {
        return (window as unknown as { GoCardlessDropin?: GoCardlessDropinSdk }).GoCardlessDropin;
    }
}
